// Copyright (c) StrangeBee 2024

import { StorageFilterEnum, UseStoragePageReturn, useStoragePage } from '@Hooks/useStoragePage';
import React, { ReactElement, ReactNode, ReactText, useEffect, useMemo, useState } from 'react';
import {
    FilterInput,
    ObjectTypeV1 as ObjectType,
    ObjectTypeV1ToV0,
    AggregationOption,
    QuerySelector,
    ParentController,
    QueryParams,
} from 'thehive-sdk';
import useFilters, { ManageFiltersOutput } from '@Hooks/useFilters';
import useListQuery from '@Hooks/useListQuery';
import useSort, { SortDirections, UseSortReturn } from '@Hooks/useSort';
import { ColumnType } from 'antd/lib/table';
import useQuickFilters, { QuickFiltersAvailable } from '@Hooks/useQuickFilters';
import { QuickFilterInterface } from '@Molecules/QuickFilterList/QuickFilterList';
import { actionListRenderer, ActionListRendererParams } from '@Helpers/actionListRenderer';
import { LayoutPaginationOptions } from '@Types';
import { classList } from '@Helpers/className';
import usePagination from '@Hooks/usePagination';
import { RefetchType, useGenericCountQuery } from '@Queries/genericQueries';
import { MultiField } from './helper/MultiField';
import { TableField, TitleSortableField } from './helper/type';
import { columnsRenderer } from './helper/computeHeader';
import { TableRowSelection } from 'antd/lib/table/interface';
import { useCurrentUser } from '@Queries/userQueries';
import { useStorageView, UseStorageViewReturn } from '@Hooks/useStorageView';
import { useStreamObserver } from '@Hooks/useStream';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { useListAllFields } from '@Queries/describeQueries';
import { QueryStatus } from 'react-query';

interface QuickFilterField {
    title?: string | { label: string; count: number };
    onClick?: (quickFilters: QuickFiltersAvailable) => void;
    divider?: boolean;
}

interface SortListField {
    label: string;
    field: string;
    sortOrder: SortDirections;
    divider?: boolean;
}

export interface RowSelection<T> {
    selectedLabel?: string;
    checkboxCell?: (data: T, antdCheckbox: ReactNode) => ReactElement;
}

export interface RowSelectionCustom<T> {
    label?: string;
    render?: (data: T, antdCheckbox: ReactNode, quickFilters: QuickFiltersAvailable) => ReactElement;
}
export interface AdditionalTableConfig<T> {
    quickFilterList?: QuickFilterInterface[];
    rowSelection?: RowSelection<T>;
    actionList?: ActionListRendererParams<T>;
    extraAction?: ReactElement;
}

export interface ConfigList<T> {
    storagePageManager?: UseStoragePageReturn;
    storageViewManager?: UseStorageViewReturn;
    filterManager: ManageFiltersOutput;
    paginationManager?: LayoutPaginationOptions;
    sortManager: UseSortReturn;

    table: {
        isLoading?: boolean;
        count?: number;
        selectedKeys?: ReactText[];
        setSelectedKeys?: React.Dispatch<React.SetStateAction<React.ReactText[]>>;
        listRefetch: RefetchType<T[] | undefined>;
        countRefetch: RefetchType<number | undefined>;
    };

    stateSelectedKeys: {
        selectedKeys: ReactText[];
        setSelectedKeys: React.Dispatch<React.SetStateAction<React.ReactText[]>>;
    };
    stateNeedRefresh: {
        needRefresh: boolean;
        setNeedRefresh: React.Dispatch<React.SetStateAction<boolean>>;
    };

    dataSource?: T[];
    listRefetch: RefetchType<T[] | undefined>;
    countRefetch: RefetchType<number | undefined>;
    status?: QueryStatus;

    rowSelectionRenderer: (rowSelection: RowSelection<T>) => RowSelection<T>;
    quickFilterListRenderer: (entries: QuickFilterField[]) => QuickFilterInterface[];
    actionListRenderer: (actionList: ActionListRendererParams<T>) => ActionListRendererParams<T>;
    columnsRenderer: (fields: TableField<T>[]) => ColumnType<T>[];
    additionalRowRenderer: (
        callback: (itemData: T, quickFiltersAvailable: QuickFiltersAvailable) => ReactElement,
    ) => (data: T) => ReactElement;
    rowSelectionCustomRenderer: (rowSelection: RowSelectionCustom<T>) => TableRowSelection<T>;
    sortListRenderer: (entries: SortListField[]) => QuickFilterInterface[];

    quickFilters: QuickFiltersAvailable;
    queryParams: QueryParams;
}

export interface UseConfigListParams {
    selector: QuerySelector;
    selectorCustomCount?: QuerySelector;
    controller: ParentController;
    aggregation?: AggregationOption;
    storageId?: StorageFilterEnum;
    objectType: ObjectType;
    fetchOnStart?: boolean;
    pagination?: 'manual' | 'on' | 'off';
    defaultView?: string;
    manual?: boolean;
    streamRootId?: string;
    streamObjectType?: ObjectType;
}

const useConfigList = <T,>({
    controller,
    selector,
    selectorCustomCount,
    aggregation,
    fetchOnStart,
    storageId,
    objectType,
    pagination = 'on',
    defaultView,
    manual = false,
    streamRootId,
    streamObjectType,
}: UseConfigListParams): ConfigList<T> => {
    const { data } = useCurrentUser();
    const { data: describeData } = useListAllFields();

    /**
     * Storage utilities
     */
    const storageViewManager = useStorageView({
        organisation: data?.organisation || '',
        fieldScope: storageId,
    });
    const storagePageManager = useStoragePage({
        organisation: data?.organisation || '',
        fieldScope: storageId,
    });

    const { page: storagePage } = storagePageManager || { page: null };

    const confSort =
        storagePage?.sortList && storagePage?.sortList?.length ? storagePage?.sortList : selector.sort || [];
    const confFilter = useMemo(
        () => (storagePage?.filters && storagePage?.filters?.length ? storagePage?.filters : []),
        [storagePage?.filters],
    );
    const confItemByPage = storagePage?.itemsPerPage || 30;
    const confAutoRefresh = storagePage?.autoRefresh;

    const filterManager = useFilters(confFilter);
    const { page, setPagination } = usePagination(confItemByPage);
    const { sortList, addSort, getFieldSort } = useSort(confSort);
    const [selectedKeys, setSelectedKeys] = useState<ReactText[]>([]);

    useEffect(() => {
        filterManager.setFilters(confFilter);
        filterManager.setTempFilters(confFilter);
    }, [storagePage]); //eslint-disable-line

    useEffect(() => {
        if (selector.defaultFilters?.length && !confFilter.length) {
            storagePageManager?.setFilters(selector.defaultFilters);
            filterManager.setTempFilters(selector.defaultFilters);
        }
    }, [selector.defaultFilters]); //eslint-disable-line

    useEffect(() => {
        if (defaultView && storageViewManager?.views[defaultView]) {
            storagePageManager?.loadView(defaultView, storageViewManager.views[defaultView]);
        }
    }, [defaultView]); //eslint-disable-line

    const quickFilters = useQuickFilters({
        fieldName: objectType,
        onChange: (newQuickFilter, concatenate = false): void => {
            setPagination((pagination) => ({ ...pagination, page: 0 }));
            filterManager.setEditFilters(true);
            filterManager.setFilters((filters) => {
                const oldFilter = filters.find(({ field }) => field === newQuickFilter.field);

                const newFilters = [
                    ...filters.filter(({ field: filtersField }) => filtersField !== newQuickFilter.field),
                    concatenate && oldFilter
                        ? {
                              ...oldFilter,
                              values: Array.from(new Set([...oldFilter?.values, ...newQuickFilter.values])),
                          }
                        : newQuickFilter,
                ];

                filterManager.setTempFilters(newFilters);
                storagePageManager?.setFilters(newFilters as FilterInput[]);
                return newFilters;
            });
        },
    });

    /**
     * List fetching
     */

    const processedFilters = useMemo(
        () =>
            confFilter.map((filter) => ({
                ...filter,
                describeAttribute: describeData?.[objectType].attributes.find(
                    ({ name }) => name === filter.field,
                ) as any,
            })),
        [describeData, confFilter, objectType],
    );

    const queryParams: QueryParams = {
        paginationOptions: pagination === 'on' ? { page, itemsPerPage: confItemByPage } : undefined,
        aggregation,
        filters: processedFilters,
        selector,
        sort: pagination === 'on' ? sortList : undefined,
    };

    const {
        data: dataSource,
        isFetching: isLoading,
        refetch: listRefetch,
        status,
    } = useListQuery<T[]>({
        controller,
        queryParams,
        fetchOnStart,
        manual,
    });

    /**
     * Count fetching
     */
    const queryCountParams = {
        controller,
        queryParams: {
            filters: processedFilters,
            selector: selectorCustomCount || selector,
        },
        fetchOnStart,
        manual,
    };

    const { data: count = 0, refetch: countRefetch } = useGenericCountQuery(queryCountParams, !!selectorCustomCount);

    /**
     * Stream update
     */
    const [needRefresh, setNeedRefresh] = useState<boolean>(false);

    const rootOpts = streamRootId ? { rootId: streamRootId } : {};

    useStreamObserver(
        { objectType: ObjectTypeV1ToV0[objectType] || streamObjectType || objectType, ...rootOpts },
        (audits) => {
            const audit = audits.find(Boolean);

            if (audit?.base._createdBy === data?.login) {
                return;
            }

            if (confAutoRefresh) {
                listRefetch();
                countRefetch();
            } else {
                setNeedRefresh(true);
            }
        },
        [confAutoRefresh, data?.login],
    );

    /**
     * Row selection renderer
     */
    const rowSelectionRenderer = (rowSelection: RowSelection<T>): RowSelection<T> => rowSelection;

    const rowSelectionCustomRenderer = (rowSelection: RowSelectionCustom<T>): TableRowSelection<T> => ({
        selectedRowKeys: selectedKeys,
        onChange: (selectedRowKeys): void => {
            setSelectedKeys?.(selectedRowKeys);
        },
        renderCell: rowSelection.render
            ? (_, record, __, checkbox): ReactElement | undefined =>
                  rowSelection.render?.(record, checkbox, quickFilters)
            : undefined,
    });

    /**
     * Quickfilter list renderer
     */
    const quickFilterListRenderer = (entries: QuickFilterField[]): QuickFilterInterface[] =>
        entries.map(({ title, onClick, divider }) => {
            if (divider) {
                return { divider: true };
            }

            const TitleRender =
                typeof title === 'string' ? (
                    <>{title}</>
                ) : (
                    <div className="space-between-flex">
                        <span>{title?.label}</span>
                        <span>{title?.count}</span>
                    </div>
                );
            return {
                label: TitleRender,
                callback: (): void => {
                    filterManager.clearFilters();
                    storagePageManager?.setFilters([]);
                    setPagination({ itemsPerPage: confItemByPage, page: 0 });
                    onClick?.(quickFilters);
                },
            };
        });

    /**
     * Sort list renderer
     */
    const sortListRenderer = (entries: SortListField[]): QuickFilterInterface[] =>
        entries.map(({ label, field, sortOrder, divider }) => {
            if (divider) {
                return { divider: true };
            }

            return {
                label,
                callback: (): void => {
                    const newSortList = addSort(field, sortOrder);
                    storagePageManager?.setSort(newSortList);
                },
            };
        });
    /********************************************************
     * Columns renderer
     *******************************************************/

    const onSortChangeHandler = ({ sortField }: TitleSortableField, newDirection: SortDirections): void => {
        if (sortField) {
            storagePageManager?.setSort(addSort(sortField, newDirection));
        }
    };

    const treatValueHandler = ({ sortField }: TitleSortableField): SortDirections =>
        sortField ? getFieldSort(sortField) : SortDirections.NONE;

    const treatDataConfigList = ({
        render,
        title,
        className,
        optionalRender,
        ...rest
    }: TableField<T>): ColumnType<T> => ({
        title: Array.isArray(title) ? (
            <ColumnTitle>
                <MultiField title={title} onSortChange={onSortChangeHandler} treatSortValue={treatValueHandler} />
                {optionalRender?.()}
            </ColumnTitle>
        ) : (
            <ColumnTitle>
                {title}
                {optionalRender?.()}
            </ColumnTitle>
        ),
        render: (value, props: T): ReactElement | null => render?.(props, quickFilters) || null,
        className: classList('custom-table__cell', className),
        ...rest,
    });

    const customColumnsRender = (fields: TableField<T>[]): ColumnType<T>[] =>
        columnsRenderer<T>(fields, treatDataConfigList);

    /********************************************************
     * additionalRowRenderer
     *******************************************************/
    const additionalRowRenderer =
        (quickFilters: QuickFiltersAvailable) =>
        (
            callback: (itemData: T, quickFiltersAvailable: QuickFiltersAvailable) => ReactElement,
        ): ((data: T) => ReactElement) =>
        (data: T): ReactElement =>
            callback(data, quickFilters);

    let outPutConfigList: ConfigList<T> = {
        rowSelectionRenderer,
        rowSelectionCustomRenderer,
        additionalRowRenderer: additionalRowRenderer(quickFilters),
        actionListRenderer,
        columnsRenderer: customColumnsRender,
        quickFilterListRenderer,
        sortListRenderer,

        dataSource,
        listRefetch,
        status,
        countRefetch,

        table: {
            isLoading,
            count,
            selectedKeys,
            setSelectedKeys,
            listRefetch,
            countRefetch,
        },

        filterManager: {
            ...filterManager,
            removeFilter: (index): void => {
                filterManager.removeFilter(index);
                const filtersProcessed = [...filterManager.filters];
                filtersProcessed.splice(index, 1);
                storagePageManager?.setFilters(filtersProcessed as FilterInput[]);
            },
            setFilters: (callback): void => filterManager.setFilters((filters) => callback(filters)),
            applyFilters: (): void => {
                storagePageManager?.setFilters(filterManager.tempFilters as FilterInput[]);
                filterManager.applyFilters();
            },
            clearFilters: (): void => {
                storagePageManager?.setFilters([]);
                filterManager.clearFilters();
            },
        },
        paginationManager:
            pagination !== 'off'
                ? {
                      page,
                      itemsPerPage: confItemByPage,
                      onPageChange: (page: number): void => {
                          setPagination((prevValues) => ({ ...prevValues, page }));
                      },
                      onItemsPerPageChange: (itemsPerPage): void => {
                          setPagination({ itemsPerPage, page: 0 });
                          storagePageManager?.setItemsPerPage(itemsPerPage);
                      },
                      setPagination,
                  }
                : undefined,
        sortManager: {
            sortList,
            addSort: (field: string, sortOrder: SortDirections): string[] => {
                const newSortList = addSort(field, sortOrder);
                storagePageManager?.setSort(newSortList);
                return newSortList;
            },
            getFieldSort,
        },

        stateSelectedKeys: {
            selectedKeys,
            setSelectedKeys,
        },
        stateNeedRefresh: {
            needRefresh,
            setNeedRefresh,
        },

        quickFilters,
        queryParams,
    };

    if (storagePageManager) {
        outPutConfigList = { ...outPutConfigList, storagePageManager };
    }

    if (storageViewManager) {
        outPutConfigList = { ...outPutConfigList, storageViewManager };
    }

    return outPutConfigList;
};

export default useConfigList;
